import { computed, reactive } from "@vue/composition-api";

import {
  download_initial_state,
  resetDownload,
} from "@/components/common/ProgressBar";
import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";

// Identifies this page as the origin of a generation request, for tracking.
const RMR_ORIGIN_PAGE_ID = 89;
const RAPPEL_CONSO_SOURCE = "Rappel Conso";

export const GENERATION_STEPS = {
  mdiBrain: "Analyse et génération des angles par l'IA",
  mdiSendClockOutline: "Mise en file d'attente des brouillons",
};

const MIN_TOPIC_LENGTH = 3;

const pickValidTopic = (...candidates) => {
  for (const candidate of candidates) {
    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      if (trimmed.length >= MIN_TOPIC_LENGTH) {
        return trimmed.slice(0, 250);
      }
    }
  }
  return null;
};

/**
 * AI-driven angle generation and draft submission for the "Veille globale" tab.
 * Generates an angle per source (without external enrichment) and creates drafts.
 *
 * @param {Object} params
 * @param {import("@vue/composition-api").ComputedRef<Object[]>} params.selectedItems
 * @param {Function} [params.onSubmitted] - called once a batch has been sent
 */
export function useGlobalWatchSubmit({
  selectedItems,
  onSubmitted,
}) {
  const { axiosGet, axiosPost } = useAxios();
  const { store, router } = useProxy();

  const state = reactive({
    loading: false,
    sentCount: 0,
    totalToSend: 0,
  });

  const download = reactive(download_initial_state());

  const snackbar = reactive({
    show: false,
    message: "",
    color: "success",
    details: [],
    actions: [],
    // -1 means "stays until dismissed": only for messages carrying an action.
    timeout: -1,
  });

  /** Transient message with no action: it must be able to dismiss itself. */
  const notify = (message, color) => {
    snackbar.message = message;
    snackbar.color = color;
    snackbar.details = [];
    snackbar.actions = [];
    snackbar.timeout = 6000;
    snackbar.show = true;
  };

  // =>  COMPUTED
  const selectedTemplateId = computed(() => {
    const value = Number(
      store.getters["aiFactory/getCustomPromptTemplate"] || 0
    );
    return value > 0 ? value : null;
  });

  const imageHandler = computed(
    () => store.getters["aiFactory/getGenerationImageHandler"] || "generate"
  );

  const selectedSiteId = computed(() =>
    Number(store.getters["aiFactory/getSelectedSites"] || 0)
  );

  const isRappelConsoSelection = computed(
    () =>
      selectedItems.value.length > 0 &&
      selectedItems.value.every(
        (item) => item.sourceName === RAPPEL_CONSO_SOURCE
      )
  );

  const isSending = computed(() => state.loading);

  const sendProgress = computed(() =>
    state.totalToSend > 1
      ? `${state.sentCount} / ${state.totalToSend} envoyés`
      : ""
  );

  // =>  METHODS
  const mapSourcesForQueue = (sources = []) =>
    sources.map((source) => ({
      url: source.url,
      // Rappel Conso payloads carry no usable body text.
      content:
        source.source !== RAPPEL_CONSO_SOURCE && source.description
          ? source.description
          : null,
    }));

  const showResultSnackbar = (results, redirectSiteId) => {
    const successCount = results.filter((r) => r.status === "success").length;
    const errorCount = results.length - successCount;

    snackbar.message = `✅ ${successCount} brouillon(s) généré(s) avec succès · ❌ ${errorCount} en erreur`;
    snackbar.color = errorCount > 0 ? "error" : "success";
    snackbar.details = results;
    // Carries actions: navigate away or close.
    snackbar.timeout = -1;
    snackbar.actions = [
      {
        text: "Voir les brouillons",
        onClick: () =>
          router.push({
            name: "ai-factory-post-generation-list",
            query: redirectSiteId ? { site_id: redirectSiteId } : {},
          }),
      },
      {
        text: "Fermer",
        onClick: () => {
          snackbar.show = false;
        },
      },
    ];
    snackbar.show = true;
  };

  /**
   * The queue endpoint takes a single article per call, so this loops.
   */
  const sendDraftPayloads = async (payloads, redirectSiteId, initialResults = []) => {
    const results = [...initialResults];

    state.loading = true;
    state.sentCount = 0;
    state.totalToSend = payloads.length;

    for (const payload of payloads) {
      try {
        await axiosPost(
          "/api/ai-factory/add-to-post-generation-queue-and-flag-source",
          payload
        );
        results.push({ topic: payload.request.topic, status: "success" });
      } catch (error) {
        console.error(error);
        results.push({ topic: payload.request.topic, status: "error" });
      }
      state.sentCount += 1;
    }

    state.loading = false;
    state.totalToSend = 0;

    showResultSnackbar(results, redirectSiteId);

    if (typeof onSubmitted === "function") {
      onSubmitted(results);
    }
  };

  const buildRequest = (overrides) => ({
    status: "draft",
    user_email: localStorage.getItem("userEmail") || null,
    image_handler: imageHandler.value,
    rmr_origin_page_id: RMR_ORIGIN_PAGE_ID,
    ...(selectedTemplateId.value
      ? { aif_custom_prompt_template_id: selectedTemplateId.value }
      : {}),
    ...overrides,
  });

  /**
   * Calls AI to generate editorial angles (with no third-party enrichment),
   * then directly queues the drafts with only the original source.
   */
  const submitArticles = async () => {
    if (selectedItems.value.length === 0 || state.loading) return;

    if (!selectedSiteId.value) {
      notify(
        "Veuillez sélectionner un site avant de lancer la génération.",
        "warning"
      );
      return;
    }

    state.loading = true;
    download.running = true;

    try {
      const selectedIds = selectedItems.value.map((item) => item.id);

      // 1. Appel IA pour générer les angles (sans enrichissement externe)
      const { data } = await axiosGet(
        "/news-radar-services/sources_curator/get_related_sources",
        {
          news_radar_data_ids: selectedIds.join(","),
          with_related_sources: false,
        }
      );

      const angleMap = new Map(
        (data || []).map((item) => [Number(item.id), item.sources?.angle])
      );

      // 2. Préparation des payloads : angle généré et source initiale uniquement
      const payloads = [];
      const skippedResults = [];

      for (const item of selectedItems.value) {
        const generatedAngle = angleMap.get(Number(item.id));
        const topic = pickValidTopic(generatedAngle, item.title);

        if (!topic) {
          skippedResults.push({
            topic: `Article #${item.id} (aucun sujet valide)`,
            status: "error",
          });
          continue;
        }

        payloads.push({
          nrd_id: item.id,
          request: buildRequest({
            site_id: selectedSiteId.value,
            topic,
            sources: mapSourcesForQueue([
              {
                url: item.url,
                source: item.sourceName,
                description: item.description,
              },
            ]),
          }),
        });
      }

      // 3. Envoi direct des brouillons dans la file
      await sendDraftPayloads(payloads, selectedSiteId.value, skippedResults);
    } catch (error) {
      console.error("Erreur lors de la génération des angles ou des brouillons", error);
      notify("La génération des brouillons a échoué.", "error");
    } finally {
      resetDownload(download);
      state.loading = false;
    }
  };

  /** Merge several Rappel Conso items into a single aggregated draft. */
  const submitAggregated = async () => {
    if (selectedItems.value.length < 2 || state.loading) return;

    if (!selectedSiteId.value) {
      notify(
        "Veuillez sélectionner un site avant de lancer la génération.",
        "warning"
      );
      return;
    }

    state.loading = true;
    download.running = true;

    try {
      const payload = {
        nrd_id: selectedItems.value[0].id,
        request: buildRequest({
          site_id: selectedSiteId.value,
          topic: "Synthèse de plusieurs rappels conso",
          sources: mapSourcesForQueue(
            selectedItems.value.map((item) => ({
              url: item.url,
              source: item.sourceName,
              description: item.description,
            }))
          ),
          is_aggregated: true,
        }),
      };

      await sendDraftPayloads([payload], selectedSiteId.value);
    } catch (error) {
      console.error(error);
      notify("La génération de la synthèse a échoué.", "error");
    } finally {
      resetDownload(download);
      state.loading = false;
    }
  };

  return {
    download,
    isRappelConsoSelection,
    isSending,
    selectedSiteId,
    sendProgress,
    snackbar,
    state,
    submitAggregated,
    submitArticles,
  };
}
